import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CHAPTER_POSTERS, CHAPTERS } from "../../lib/content";

const TOTAL_CHAPTERS = 15;

function CarouselDot({ active, released, onClick, n }) {
  let cls = "h-1.5 rounded-full transition-all ";
  if (active) cls += "w-8 bg-gold";
  else if (released) cls += "w-2 bg-zinc-500 hover:bg-zinc-300";
  else cls += "w-2 bg-zinc-700 hover:bg-zinc-500";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`chapter-dot-${n}`}
      aria-label={`Go to chapter ${n}`}
      className={cls}
    />
  );
}

export default function ChapterCarousel() {
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  // Build full chapter list: real posters + placeholders for not-yet-released
  const slides = Array.from({ length: TOTAL_CHAPTERS }, (_, i) => {
    const n = i + 1;
    const poster = CHAPTER_POSTERS.find((p) => p.n === n);
    const chapter = CHAPTERS[i];
    return {
      n,
      title: chapter?.title || `Chapter ${n}`,
      quote: chapter?.quote || "",
      note: chapter?.note || "",
      img: poster?.img || null,
      released: !!poster
    };
  });

  const scrollToIdx = (idx) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[idx];
    if (!child) return;
    const left = child.offsetLeft - (track.clientWidth - child.clientWidth) / 2;
    track.scrollTo({ left, behavior: "smooth" });
  };

  const onPrev = () => {
    const next = Math.max(0, activeIdx - 1);
    setActiveIdx(next);
    scrollToIdx(next);
  };
  const onNext = () => {
    const next = Math.min(slides.length - 1, activeIdx + 1);
    setActiveIdx(next);
    scrollToIdx(next);
  };

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let closestDist = Infinity;
        Array.from(track.children).forEach((c, i) => {
          const cCenter = c.offsetLeft + c.clientWidth / 2;
          const d = Math.abs(cCenter - center);
          if (d < closestDist) { closestDist = d; closest = i; }
        });
        setActiveIdx(closest);
        ticking = false;
      });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  const releasedCount = CHAPTER_POSTERS.length;

  return (
    <div className="relative mx-auto mt-24 max-w-7xl px-6 md:mt-32 md:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-10 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end"
      >
        <div>
          <div className="mb-3 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
            <span className="h-px w-10 bg-gold/60" />
            Chapter Posters
          </div>
          <h3 className="font-display text-3xl font-bold leading-tight text-white md:text-4xl lg:text-5xl">
            <span className="text-gradient-gold">{TOTAL_CHAPTERS}</span> chapters. <span className="text-gradient-gold">{TOTAL_CHAPTERS}</span> blueprints.
          </h3>
          <p className="mt-3 max-w-xl text-sm text-zinc-400">
            Visual overviews for every chapter — scripture, mantra, mindset, and disciplines. {releasedCount} of {TOTAL_CHAPTERS} released.
          </p>
        </div>
        <div className="flex items-center gap-2" data-testid="chapter-carousel-controls">
          <button
            type="button"
            onClick={onPrev}
            disabled={activeIdx === 0}
            data-testid="chapter-prev"
            aria-label="Previous chapter"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-gold/40 hover:text-gold disabled:opacity-40"
          >
            <i className="fa-solid fa-arrow-left" />
          </button>
          <div className="rounded-full border border-gold/30 px-4 py-2 text-xs uppercase tracking-[0.25em] text-gold">
            {String(activeIdx + 1).padStart(2, "0")} / {String(TOTAL_CHAPTERS).padStart(2, "0")}
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={activeIdx === slides.length - 1}
            data-testid="chapter-next"
            aria-label="Next chapter"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 text-zinc-300 transition-colors hover:border-gold/40 hover:text-gold disabled:opacity-40"
          >
            <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      </motion.div>

      {/* Track */}
      <div
        ref={trackRef}
        data-testid="chapter-carousel-track"
        className="no-scrollbar scroll-fade-mask flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4"
        style={{ scrollPaddingInline: "8%" }}
      >
        {slides.map((s, i) => {
          const isActive = i === activeIdx;
          return (
            <div
              key={s.n}
              data-testid={`chapter-slide-${s.n}`}
              onClick={() => { setActiveIdx(i); scrollToIdx(i); }}
              className={`group relative shrink-0 cursor-pointer snap-center transition-all duration-500 ${
                isActive ? "w-[72vw] md:w-[360px] lg:w-[420px]" : "w-[56vw] md:w-[280px] lg:w-[330px] opacity-60 hover:opacity-90"
              }`}
            >
              <div
                className={`relative aspect-[2/3] overflow-hidden rounded-3xl border ${
                  isActive ? "border-gold/50 shadow-[0_30px_80px_-20px_rgba(197,160,89,0.45)]" : "border-white/5"
                } bg-ink-700`}
              >
                {s.released && s.img ? (
                  <img
                    src={s.img}
                    alt={`Chapter ${s.n} — ${s.title}`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-ink-700 to-ink-900 p-8 text-center">
                    <div className="font-display text-7xl text-gold/40 md:text-8xl">
                      {String(s.n).padStart(2, "0")}
                    </div>
                    <div className="mt-4 max-w-xs font-display text-xl leading-snug text-white">
                      {s.title}
                    </div>
                    <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-black/40 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-gold">
                      <i className="fa-solid fa-hourglass-half text-[10px]" />
                      Releasing Soon
                    </div>
                  </div>
                )}
                {/* Chapter number badge */}
                <div className="absolute left-4 top-4 flex h-10 items-center gap-2 rounded-full border border-gold/40 bg-black/70 px-3 text-xs uppercase tracking-[0.25em] text-gold backdrop-blur">
                  <span className="font-display text-base">{String(s.n).padStart(2, "0")}</span>
                  <span className="hidden sm:inline">{s.released ? "Released" : "Soon"}</span>
                </div>
              </div>
              {/* Caption */}
              {isActive && (
                <div className="mt-5 text-center">
                  <div className="font-display text-xl text-white md:text-2xl">{s.title}</div>
                  {s.quote && (
                    <div className="mt-2 font-display italic text-gold-pale">"{s.quote}"</div>
                  )}
                  {s.note && (
                    <div className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-500">{s.note}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Dots */}
      <div className="mt-6 flex flex-wrap justify-center gap-1.5">
        {slides.map((s, i) => (
          <CarouselDot
            key={s.n}
            n={s.n}
            active={i === activeIdx}
            released={s.released}
            onClick={() => { setActiveIdx(i); scrollToIdx(i); }}
          />
        ))}
      </div>
    </div>
  );
}
